/* One place to answer "which column of this result row holds X?".
 *
 * ── Why this module exists ────────────────────────────────────────────────
 * Four call sites grew four different answers, and the differences were
 * invisible because every one of them returns 0 rather than throwing when it
 * cannot resolve a column:
 *
 *   ConsumptionChart / OverlayChart getColumnValue  exact key only
 *   PricingChart      getValue                      exact key only
 *   OverlayChart      getValue                      case-SENSITIVE substring
 *   TransferChart     getValue                      case-INSENSITIVE substring
 *   TransferDailyRangeChart getTransferValue        case-INSENSITIVE substring
 *
 * On a result whose header casing differed at all, Transfer would resolve a
 * column and plot a line while Summary silently returned 0 for the same run,
 * with no error anywhere.
 *
 * ── The matching policy, and why ──────────────────────────────────────────
 * DEFAULT IS EXACT. Not the most permissive option — the safest one that the
 * data supports.
 *
 * The tempting argument for substring matching is that it lets key
 * "BESS Charge" find column "BESS Charge (kWh)". No call site in this app
 * actually does that: every key passed here is a complete column name,
 * including its unit suffix. So substring matching buys nothing at any
 * existing site, while costing real safety at one of them:
 *
 *   key "Load (kWh)" matches FIVE columns of a husky result under substring —
 *   "Building Load Load (kWh)", "Terminal Solar To Load (kWh)",
 *   "Terminal BESS To Load (kWh)", "<EV group> To Load (kWh)", ... —
 *   and Object.keys() order decides which one wins. It lands on the right one
 *   today only because the sim happens to emit the Building Load group first.
 *   Reorder the CSV groups and the Consumption chart plots solar-to-load while
 *   calling it building load, with nothing to indicate anything went wrong.
 *
 * Measured against every result file in Grene.DT.sim Prod/projects
 * (185 files, 6 distinct header shapes): exact, case-sensitive substring,
 * case-insensitive substring and the policy below all resolve every key set
 * used by every chart to the SAME column. Substring is not load-bearing on any
 * result the sim produces. See __tests__/columnLookup.test.js, which pins the
 * six real header shapes and asserts they agree.
 *
 * ── The escape hatch, exposed rather than papered over ────────────────────
 * The Transfer charts' fallback chains carry bare legacy names
 * ("Energy Buy/Sell (kWh)", "Cost or Return ($)") meant to tolerate result
 * files this repo has no example of. Removing their looseness outright would
 * be a behaviour change nobody can verify, so it stays — but as an explicit,
 * named opt-in rather than the default, and it runs only AFTER the whole
 * fallback chain has failed to match exactly:
 *
 *   readColumn(entry, keys)                       exact only        (default)
 *   readColumn(entry, keys, { fallback: LOOSE })  exact, then loose
 *
 * Exact-first is what makes the loose pass safe: "Load (kWh)" can never reach
 * it, because "Building Load Load (kWh)" sits in the same chain and matches
 * exactly first. A site that must never guess simply omits the option.
 *
 * RESIDUAL RISK, stated plainly: the loose pass is still first-match-wins over
 * Object.keys() order. If a future result file both (a) fails every exact key
 * in a chain and (b) contains more than one loose candidate, the column picked
 * is decided by CSV column order. That is strictly better than today (where
 * three sites reach that state on the first key rather than the last) but it is
 * not zero. resolveColumnKey() below returns the candidate count so a caller
 * that wants to surface the ambiguity can.
 */

/** Fallback strictness for readColumn / resolveColumnKey. */
export const EXACT = "exact";
export const LOOSE = "loose";

/* Probe several rows for a column rather than only row 0: one ragged row would
 * otherwise disable a column for a whole period. Same constant and reasoning as
 * public/workers/metrics.worker.js, which this module is deliberately aligned
 * with — see resolveStorageFlowKeys below. */
const KEY_PROBE_ROWS = 50;

const toFiniteNumber = (raw) => {
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
};

const isPresent = (value) => value != null && value !== "";

/* A key containing "-" is a difference expression: "ColA-ColB" means
 * value(ColA) - value(ColB). Both sides are resolved by exact key only, never
 * loosely — a guessed column on either side of a subtraction is a wrong number
 * that looks entirely plausible. */
const isDifferenceExpression = (key) => key.includes("-");

/**
 * Resolve which column of `columnNames` a fallback chain selects.
 *
 * @param {string[]} columnNames - Object.keys(row), in CSV column order.
 * @param {string[]} candidateKeys - fallback chain, most specific first.
 * @param {string} fallback - EXACT (default) or LOOSE.
 * @returns {{key: string|null, expression: {left: string, right: string}|null,
 *            matchedBy: "exact"|"loose"|"difference"|null, candidates: number}}
 *   `candidates` counts how many columns the loose pass could have chosen from;
 *   anything above 1 means the answer depended on column order.
 */
export function resolveColumnKey(columnNames, candidateKeys, fallback = EXACT) {
  const keys = Array.isArray(columnNames) ? columnNames : [];
  const chain = Array.isArray(candidateKeys) ? candidateKeys : [];
  const miss = { key: null, expression: null, matchedBy: null, candidates: 0 };

  /* Pass 1 — exact, over the whole chain. A difference expression counts as
   * resolved when either side is present, matching the behaviour the Transfer
   * charts already had. */
  for (const key of chain) {
    if (isDifferenceExpression(key)) {
      const [left, right] = key.split("-").map((part) => part.trim());
      if (keys.includes(left) || keys.includes(right)) {
        return {
          key: null,
          expression: { left, right },
          matchedBy: "difference",
          candidates: 1,
        };
      }
      continue;
    }
    if (keys.includes(key)) {
      return { key, expression: null, matchedBy: "exact", candidates: 1 };
    }
  }

  if (fallback !== LOOSE) return miss;

  /* Pass 2 — case-insensitive substring, over the whole chain again. Reached
   * only when nothing matched exactly. Case-insensitive rather than the
   * case-sensitive variant OverlayChart used, because the divergence this
   * module exists to remove is exactly "Transfer resolves it, Summary does
   * not"; the two agree only if Summary widens to match Transfer. */
  for (const key of chain) {
    if (isDifferenceExpression(key)) continue;
    const needle = key.toLowerCase();
    const matches = keys.filter((name) => name.toLowerCase().includes(needle));
    if (matches.length > 0) {
      return {
        key: matches[0],
        expression: null,
        matchedBy: "loose",
        candidates: matches.length,
      };
    }
  }

  return miss;
}

/**
 * Read a numeric value out of one result row using a fallback chain.
 *
 * Replaces ConsumptionChart.getColumnValue, OverlayChart.getColumnValue,
 * OverlayChart.getValue, PricingChart.getValue, TransferChart.getValue and
 * TransferDailyRangeChart's inline lookup.
 *
 * Non-numeric, absent and empty values all read as 0, as every one of those
 * call sites already did — charts plot a zero, they do not blow up on a ragged
 * row.
 *
 * @param {object} entry - one parsed result row.
 * @param {string[]} candidateKeys - fallback chain, most specific first.
 * @param {{fallback?: string}} [options] - pass { fallback: LOOSE } to allow
 *   substring matching after every exact key has failed.
 * @returns {number}
 */
export function readColumn(entry, candidateKeys, options) {
  if (!entry || typeof entry !== "object") return 0;
  const fallback = options?.fallback ?? EXACT;
  const resolved = resolveColumnKey(
    Object.keys(entry),
    candidateKeys,
    fallback,
  );

  if (resolved.matchedBy === "difference") {
    const { left, right } = resolved.expression;
    return toFiniteNumber(entry[left]) - toFiniteNumber(entry[right]);
  }

  if (resolved.key && isPresent(entry[resolved.key])) {
    return toFiniteNumber(entry[resolved.key]);
  }

  return 0;
}

/* ── Storage flow columns: the Battery / BESS split ───────────────────────
 *
 * A BESS device's columns are named after one of two conventions and the app
 * disagreed with itself about which:
 *
 *   public/workers/metrics.worker.js (the stat cards) PROBES the header —
 *     hasBatteryKey ? "Battery" : "BESS" — and so reads all four shapes.
 *   StorageChart / OverlayChart getChargeDischarge HARDCODED both names at
 *     once: "Battery" for the split columns, "BESS" for the combined one.
 *
 * The hardcoding is wrong for two of the four shapes, and wrong silently:
 *
 *   split columns named "<dev> BESS Charge/Discharge (kWh)"      cards right,
 *     -> chart reads "<dev> Battery Charge (kWh)"      -> 0        chart FLAT
 *     -> falls through to "<dev> BESS Charge/Discharge" -> 0
 *   combined column named "<dev> Battery Charge/Discharge (kWh)" cards right,
 *     -> chart reads "<dev> Battery Charge (kWh)"      -> 0        chart FLAT
 *     -> falls through to "<dev> BESS Charge/Discharge" -> 0
 *
 * In both cases the stat cards above the chart report real energy while the
 * plotted line sits on zero for the same run.
 *
 * Every result file in Grene.DT.sim Prod/projects uses "Battery <verb>" split
 * columns, so this is latent on all data we hold — no combined column and no
 * "BESS Charge" column exists anywhere in the corpus. It is fixed here rather
 * than left alone because the fallback branch is the app's stated contract for
 * single-header results and the worker already honours it.
 */

/* Anchored at the start, not a substring match: the sim always emits
 * "<device> <metric>", so a device literally named "1" must not be satisfied by
 * "Battery1 Battery Charge (kWh)" — resolving "Battery" off another device's
 * column then reads its own, absent, columns as zero. */
const hasColumnPrefix = (rows, prefix) => {
  const limit = Math.min(rows.length, KEY_PROBE_ROWS);
  for (let i = 0; i < limit; i += 1) {
    const row = rows[i];
    if (!row) continue;
    if (Object.keys(row).some((name) => name.startsWith(prefix))) return true;
  }
  return false;
};

/**
 * Decide which naming convention a device's storage columns use, and build the
 * three keys that follow from it. Probe once per period, read many times.
 *
 * The probe is deliberately the same test metrics.worker.js makes — its
 * hasPrefixInData walks the same KEY_PROBE_ROWS rows and anchors the same
 * prefix — so the stat cards and the plotted line can no longer resolve
 * different columns for the same device in the same run.
 *
 * @param {object[]} rows - the rows about to be plotted.
 * @param {string} deviceName
 * @param {"BESS"|"EV"} deviceType - EV columns have only ever been "EV <verb>";
 *   only BESS is probed, exactly as the worker does.
 * @returns {{naming: string, chargeKey: string, dischargeKey: string,
 *            combinedKey: string}}
 */
export function resolveStorageFlowKeys(rows, deviceName, deviceType) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const naming =
    deviceType === "EV"
      ? "EV"
      : hasColumnPrefix(safeRows, `${deviceName} Battery`)
        ? "Battery"
        : "BESS";

  return {
    naming,
    chargeKey: `${deviceName} ${naming} Charge (kWh)`,
    dischargeKey: `${deviceName} ${naming} Discharge (kWh)`,
    combinedKey: `${deviceName} ${naming} Charge/Discharge (kWh)`,
  };
}

/**
 * Build a per-row reader for one device's signed storage flow.
 *
 * Sign convention, unchanged: positive is discharge, negative is charge.
 *
 * The split/combined choice stays per-row rather than per-period, which is what
 * StorageChart and OverlayChart already did — a row whose split columns are
 * both 0 falls through to the combined column. Only the NAME the fallback looks
 * for changes, so on every result file we hold this reader returns exactly the
 * values the hardcoded version returned.
 *
 * @param {object[]} rows - the rows about to be plotted, used for the probe.
 * @param {string} deviceName
 * @param {"BESS"|"EV"} deviceType
 * @returns {(entry: object) => number}
 */
export function makeStorageFlowReader(rows, deviceName, deviceType) {
  const { chargeKey, dischargeKey, combinedKey } = resolveStorageFlowKeys(
    rows,
    deviceName,
    deviceType,
  );

  return (entry) => {
    if (!entry) return 0;
    const charge = Number(entry[chargeKey]) || 0;
    const discharge = Number(entry[dischargeKey]) || 0;
    if (charge !== 0 || discharge !== 0) return discharge - charge;
    return Number(entry[combinedKey]) || 0;
  };
}
